import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { BoundedApiError, type PolicyBundle } from '@bounded/api-client';
import { q } from '../../api/queries';
import { useDraft } from '../../lib/draft';
import { ErrorNotice, Provenance } from '@bounded/ui/state';
import { Button, Empty, Panel, SectionHeader, Select, Skeleton } from '@bounded/ui/primitives';
import { GraphCanvas } from '../../components/graph/graph-canvas';
import { ConfigEditor, isObject } from '@bounded/ui/config-editor';
import { NodeForm } from '../../components/graph/node-form';
import { PublishRail } from '../../components/policy/publish-rail';
import { ADDABLE, ADD_PORT, INPUTS, OUTPUT, addModuleCall, inputFor, newNode, removeInput, removeNode, validate, type Op } from '../../lib/graph-edit';
import type { AnyGraph, AnyNode } from '../../lib/graph';
import { useT } from '../../i18n';

export const Route = createFileRoute('/_app/projects/$projectId/graph')({ component: GraphPage });

function GraphPage() {
  const t = useT();
  const { projectId } = Route.useParams();
  const draft = useQuery(q.draft(projectId));
  if (draft.error instanceof BoundedApiError && draft.error.code === 'DRAFT_NOT_FOUND') {
    return <Panel><Empty title={t.policy.chooseTitle} action={<Link to="/projects/$projectId/policy" params={{ projectId }} className="text-boundary hover:underline">{t.rollout.choosePolicy}</Link>}>{t.policy.chooseDescription}</Empty></Panel>;
  }
  if (draft.error) return <ErrorNotice error={draft.error} onRetry={() => void draft.refetch()} />;
  if (!draft.data) return <Skeleton className="h-[560px]" />;
  return <GraphEditor projectId={projectId} />;
}

function GraphEditor({ projectId }: { projectId: string }) {
  const t = useT(); const m = t.editor;
  const { bundle, base, save, edit, reload, defaultsRevision } = useDraft(projectId);
  const models = useQuery(q.models());
  const hosted = useQuery(q.hostedModels());
  const [selected, setSelected] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Undo/redo over whole bundles: every edit is one step, and undo goes through the same save path.
  const past = useRef<PolicyBundle[]>([]); const future = useRef<PolicyBundle[]>([]);
  const [, bump] = useState(0);
  const apply = useCallback((next: PolicyBundle, record = true) => {
    if (!bundle) return;
    if (record) { past.current = [...past.current.slice(-49), bundle]; future.current = []; }
    setNotice(null);
    edit(next, validate(next.graph as AnyGraph, next.modules).length === 0);
    bump((n) => n + 1);
  }, [bundle, edit]);
  const undo = useCallback(() => {
    const prev = past.current.at(-1); if (!prev || !bundle) return;
    past.current = past.current.slice(0, -1); future.current = [bundle, ...future.current];
    apply(prev, false);
  }, [apply, bundle]);
  const redo = useCallback(() => {
    const [next, ...rest] = future.current; if (!next || !bundle) return;
    future.current = rest; past.current = [...past.current, bundle];
    apply(next, false);
  }, [apply, bundle]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z' || (e.target instanceof HTMLElement && e.target.closest('input,textarea,select'))) return;
      e.preventDefault(); if (e.shiftKey) redo(); else undo();
    };
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const graph = bundle?.graph as AnyGraph | undefined;
  const issues = useMemo(() => (graph && bundle ? validate(graph, bundle.modules) : []), [graph, bundle]);
  const invalid = useMemo(() => new Set(issues.map((i) => i.node).filter((x): x is string => !!x)), [issues]);
  if (!bundle || !graph) return <Skeleton className="h-[560px]" />;

  const connections = [...(models.data?.items ?? []).map((c) => ({ id: c.id, name: c.name })), ...(hosted.data?.items ?? []).map((h) => ({ id: h.id, name: t.models.providers.hosted }))];
  const change = (mutate: (node: AnyNode, graph: AnyGraph) => void) => {
    if (!selected) return;
    const next = structuredClone(bundle);
    mutate((next.graph as AnyGraph).nodes[selected], next.graph as AnyGraph);
    apply(next);
  };
  const add = (op: Op) => {
    const next = structuredClone(bundle);
    const { id, node } = newNode(next.graph as AnyGraph, op, connections[0]?.id);
    (next.graph as AnyGraph).nodes[id] = node;
    apply(next); setSelected(id);
  };
  const addModule = (hash: string) => {
    const r = addModuleCall(bundle, hash);
    apply(r.bundle); setSelected(r.id);
    if (!r.joined) setNotice(m.moduleNotJoined);
  };
  const connect = (source: string, target: string, port?: string) => {
    const from = graph.nodes[source]; const to = graph.nodes[target];
    // Dropped on an input that takes this output: use that input (on a Combine node, replace the one it was dropped on).
    // Otherwise fall back to the input this output fits.
    const onPort = !from || !to || !port || port === ADD_PORT ? null
      : to.op === 'aggregate' ? (OUTPUT[from.op] === 'proposal' && port in to.inputs ? port : null)
      : INPUTS[to.op]?.[port]?.port === OUTPUT[from.op] ? port : null;
    const name = from && to ? onPort ?? inputFor(to, source, from.op) : null;
    if (!name || !from) { setNotice(m.notConnectable); return; }
    const next = structuredClone(bundle);
    const node = (next.graph as AnyGraph).nodes[target];
    (node.inputs as Record<string, { node: string; port: string }>)[name] = { node: source, port: OUTPUT[from.op] };
    if (node.op === 'aggregate' && node.config.strategy === 'first_applicable' && !node.config.priority?.includes(name)) node.config.priority = [...(node.config.priority ?? []), name] as [string, ...string[]];
    apply(next);
  };
  const deleteNodes = (ids: string[]) => {
    let next = bundle; let dropped = 0;
    for (const id of ids) { const r = removeNode(next, id); next = r.bundle; dropped += r.droppedBindings; }
    apply(next); setSelected(null);
    if (dropped) setNotice(m.droppedBindings(dropped));
  };
  const deleteEdges = (edges: { target: string; input: string }[]) => {
    let next = bundle;
    for (const e of edges) next = removeInput(next, e.target, e.input);
    apply(next);
  };

  return (
    <div className="grid gap-6">
      <SectionHeader title={m.title} description={m.description}
        actions={<>
          <Button size="sm" disabled={!past.current.length} onClick={undo} title="⌘Z">{m.undo}</Button>
          <Button size="sm" disabled={!future.current.length} onClick={redo} title="⇧⌘Z">{m.redo}</Button>
        </>} />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-graphite">{m.add}</span>
        {ADDABLE.map((op) => <Button key={op} size="sm" onClick={() => add(op)}>{t.graph.ops[op]}</Button>)}
        {Object.keys(bundle.modules).length > 0 && (
          <div className="w-52">
            <Select aria-label={t.modules.addCall} value="" className="h-7 text-sm" onChange={(e) => { if (e.target.value) addModule(e.target.value); }}>
              <option value="">{t.modules.addCall}</option>
              {Object.entries(bundle.modules).map(([hash, mod]) => <option key={hash} value={hash}>{mod.graph_id} {mod.version}</option>)}
            </Select>
          </div>
        )}
      </div>
      {notice && <p role="status" className="rounded-[var(--radius-control)] border border-ask/40 bg-ask/5 px-3 py-2 text-sm">{notice}</p>}
      {/* The whole policy (graph, modules, settings) as JSON: applied as one undoable edit and saved like any other. */}
      <ConfigEditor id="policy-json" commit="apply" rows={30} value={bundle} onChange={(next) => { apply(next); setSelected(null); }}
        validate={(v) => (isObject(v) && v.protocol === 'bounded.policy/0.1' && isObject(v.graph) && isObject((v.graph as Record<string, unknown>).nodes) && isObject(v.modules) && isObject(v.overrides) ? null : m.notBundle)}>
      {/* The canvas gets the full width; the inspector floats over its right edge while a node is selected. */}
      <div className="relative">
        <GraphCanvas graph={graph} modules={bundle.modules} invalid={invalid} selected={selected} onSelect={setSelected} onConnect={connect} height={600}
          onDeleteNodes={(ids) => deleteNodes(ids.filter((id) => graph.nodes[id] && !['action', 'decision'].includes(graph.nodes[id].op)))}
          onDeleteEdges={deleteEdges} layoutKey={`bounded.layout.${projectId}.${graph.graph_id}`} />
        {selected && graph.nodes[selected] ? (
          <section aria-label={m.inspector} className="absolute inset-y-3 right-3 flex w-[min(22rem,calc(100%-1.5rem))] flex-col overflow-hidden rounded-[var(--radius-panel)] border border-rule bg-surface shadow-[0_8px_24px_rgb(0_0_0/0.12)]">
            <header className="flex items-center justify-between border-b border-rule px-4 py-2.5">
              <h2 className="font-semibold">{m.inspector}</h2>
              <Button size="sm" variant="ghost" className="-mr-2.5" onClick={() => setSelected(null)}>{t.common.close}</Button>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <NodeForm projectId={projectId} bundle={bundle} id={selected} change={change} connections={connections} onDelete={() => deleteNodes([selected])} />
            </div>
          </section>
        ) : (
          <p className="pointer-events-none absolute right-24 bottom-3 left-16 w-fit rounded bg-surface/90 px-2 py-1 text-xs text-graphite">{m.none}</p>
        )}
      </div>
      </ConfigEditor>
      {/* Below the canvas everything spans the full width: a tall rail beside a one-line note left most of the row empty. */}
      <PublishRail layout="bar" projectId={projectId} save={save} base={base} defaultsRevision={defaultsRevision} onReload={reload} />
      {issues.length > 0 && (
        <Panel title={m.issuesTitle(issues.length)} className="border-deny/40">
          <ul>{issues.map((i, n) => (
            <li key={n} className="flex flex-wrap items-baseline gap-x-3 border-b border-rule px-4 py-2.5 text-sm last:border-b-0">
              {i.node ? <button type="button" className="font-mono text-boundary hover:underline" onClick={() => setSelected(i.node!)}>{i.node}</button> : null}
              <span>{m.issues[i.code] ?? i.code}</span>
            </li>
          ))}</ul>
        </Panel>
      )}
      <Provenance>{m.layoutNote}</Provenance>
    </div>
  );
}
